import { Button, ButtonProps, TextButton } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import { RefreshSmall } from '@wix/wix-ui-icons-common';
import React, { FC, MouseEventHandler } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';

import { TLocalesKeys as K } from '../../../../types';
import { logChatbotStartOver } from '../../../bi';
import { sendRestartChat } from '../../../common/api/sendRestartChat';
import { TRootState } from '../../../common/reducers';
import { chatActions } from '../../../common/reducers/chat';
import { ResetReason } from '../../../common/reducers/types';
import { selectIsLocked, selectRestartChatParameters } from '../../../common/selectors/chat';
import { selectIsEnabled } from '../../../common/selectors/header';
import { selectEditorType } from '../../../common/selectors/homepage';
import { selectLoadPageUrl } from '../../../common/selectors/meta';
import { experiments } from '../../../experiments';
import { navigationControl } from '../../../util/navigationControl';
import { renderModel } from '../../../util/renderModel';
import { HeaderActionsIcon } from '../HeaderActions/HeaderActionsIcon';
import s from '../HeaderActions/styles.scss';
import { useEnableResetListener } from './useEnableResetListener';

const isCopilot = renderModel.isCopilot();

const selectors = (state: TRootState) => ({
  isEnabled: selectIsEnabled(state),
  isLocked: selectIsLocked(state),
  restartChatParameters: selectRestartChatParameters(state),
});

const resetIconEnabled = experiments.is('specs.chatbot.resetButton', 'true');

export const ResetButton: FC<ButtonProps> = (props) => {
  const { isEnabled, isLocked, restartChatParameters } = useSelector(selectors);
  const { t } = useTranslation();
  const store = useStore();
  const dispatch = useDispatch();
  const isEditor3 = renderModel.isEditor3();

  useEnableResetListener();

  const clickHandler: MouseEventHandler<HTMLElement> = (e) => {
    const state = store.getState();
    const loadPageUrl = selectLoadPageUrl(state);

    e.stopPropagation();

    logChatbotStartOver(state);

    const editorType = selectEditorType(state);
    sendRestartChat(restartChatParameters, loadPageUrl, editorType);

    dispatch(chatActions.reset(ResetReason.StartOver));
    window.scroll(0, 0);
    navigationControl.unlock();
  };

  if (!isEnabled) {
    return null;
  }

  if (isCopilot) {
    return (
      <Button
        disabled={props.disabled}
        size="small"
        onClick={isLocked ? undefined : clickHandler}
        dataHook={props.dataHook}
        skin="dark"
        priority="secondary"
      >
        {t<K>('header_btn_label')}
      </Button>
    );
  }
  if (isEditor3) {
    return (
      <TextButton
        skin="dark"
        className={s.resetButtonEditor3}
        size="tiny"
        onClick={isLocked ? undefined : clickHandler}
        tooltipContent={t<K>('header_btn_label')}
        prefixIcon={<RefreshSmall />}
      >
        {t<K>('header_btn_label')}
      </TextButton>
    );
  }

  return resetIconEnabled ? (
    <HeaderActionsIcon
      onClick={isLocked ? undefined : clickHandler}
      tooltipContent={t<K>('header_btn_label')}
      pref
    >
      <RefreshSmall />
    </HeaderActionsIcon>
  ) : (
    <Button skin="inverted" onClick={isLocked ? undefined : clickHandler} {...props}>
      {t<K>('header_btn_label')}
    </Button>
  );
};
